import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../contexts/AppContext';
import '../styles/index.css';

export default function Billing() {
  const navigate = useNavigate();
  const { user, creditBalance, subscriptionStatus, subscriptionTier, formatCredits, creditsToUSD } = useApp();
  const [packages, setPackages] = useState([]);
  const [subscriptionTiers, setSubscriptionTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('packages'); // 'packages' or 'subscription'

  useEffect(() => {
    const fetchBillingData = async () => {
      try {
        const [packagesRes, tiersRes] = await Promise.all([
          fetch('http://localhost:3001/api/billing/packages'),
          fetch('http://localhost:3001/api/billing/subscription-tiers')
        ]);

        const packagesData = await packagesRes.json();
        const tiersData = await tiersRes.json();

        setPackages(packagesData);
        setSubscriptionTiers(tiersData);
      } catch (error) {
        console.error('Error fetching billing data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBillingData();
  }, []);

  const handlePurchaseCredits = async (packageId) => {
    if (!user) {
      alert('Please sign in to purchase credits');
      return;
    }

    try {
      const response = await fetch('http://localhost:3001/api/stripe/purchase-credits', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.uid,
          packageId,
          email: user.email
        })
      });

      const data = await response.json();

      if (data.url) {
        // Redirect to Stripe Checkout
        window.location.href = data.url;
      } else {
        alert('Error creating checkout session. Please try again.');
      }
    } catch (error) {
      console.error('Error purchasing credits:', error);
      alert('Error processing payment. Please try again.');
    }
  };

  const handleSubscribe = async (tier) => {
    if (!user) {
      alert('Please sign in to subscribe');
      return;
    }

    try {
      const response = await fetch('http://localhost:3001/api/stripe/create-subscription', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.uid,
          tier,
          email: user.email
        })
      });

      const data = await response.json();

      if (data.url) {
        // Redirect to Stripe Checkout
        window.location.href = data.url;
      } else {
        alert('Error creating subscription. Please try again.');
      }
    } catch (error) {
      console.error('Error creating subscription:', error);
      alert('Error processing subscription. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <p>Loading billing information...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>Billing & Credits</h1>
          <p className="page-subtitle">Manage your subscription and purchase credits</p>
        </div>
        <button onClick={() => navigate('/usage-history')} className="btn btn-secondary">
          View Usage History
        </button>
      </div>

      {/* Current Balance Card */}
      <div className="card" style={{ marginBottom: '32px', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h3 style={{ margin: 0, marginBottom: '8px', color: 'white', opacity: 0.9 }}>Current Balance</h3>
            <div style={{ fontSize: '2.5rem', fontWeight: 700, marginBottom: '4px' }}>
              {formatCredits(creditBalance)} credits
            </div>
            <div style={{ fontSize: '1.1rem', opacity: 0.9 }}>
              ≈ ${creditsToUSD(creditBalance)} USD
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '0.9rem', opacity: 0.9, marginBottom: '4px' }}>Subscription Status</div>
            <div style={{
              fontSize: '1.1rem',
              fontWeight: 600,
              padding: '6px 12px',
              background: 'rgba(255, 255, 255, 0.2)',
              borderRadius: '6px',
              textTransform: 'capitalize'
            }}>
              {subscriptionStatus || 'inactive'}
            </div>
            {subscriptionTier && (
              <div style={{ fontSize: '0.9rem', marginTop: '4px', opacity: 0.9 }}>
                {subscriptionTier.charAt(0).toUpperCase() + subscriptionTier.slice(1)} Plan
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', borderBottom: '2px solid var(--color-border)' }}>
        <button
          onClick={() => setActiveTab('packages')}
          style={{
            padding: '12px 24px',
            background: 'none',
            border: 'none',
            borderBottom: activeTab === 'packages' ? '3px solid var(--color-primary)' : '3px solid transparent',
            color: activeTab === 'packages' ? 'var(--color-primary)' : 'var(--color-text-secondary)',
            fontWeight: activeTab === 'packages' ? 600 : 400,
            cursor: 'pointer',
            fontSize: '1rem',
            transition: 'all 0.2s'
          }}
        >
          Credit Packages
        </button>
        <button
          onClick={() => setActiveTab('subscription')}
          style={{
            padding: '12px 24px',
            background: 'none',
            border: 'none',
            borderBottom: activeTab === 'subscription' ? '3px solid var(--color-primary)' : '3px solid transparent',
            color: activeTab === 'subscription' ? 'var(--color-primary)' : 'var(--color-text-secondary)',
            fontWeight: activeTab === 'subscription' ? 600 : 400,
            cursor: 'pointer',
            fontSize: '1rem',
            transition: 'all 0.2s'
          }}
        >
          Subscription Plans
        </button>
      </div>

      {/* Credit Packages Tab */}
      {activeTab === 'packages' && (
        <div>
          <div style={{ marginBottom: '24px' }}>
            <h2>Purchase Credits</h2>
            <p style={{ color: 'var(--color-text-secondary)' }}>
              Credits never expire. Use them for AI-powered creative generation.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
            {packages.map((pkg) => (
              <div key={pkg.id} className="card" style={{
                border: pkg.id === 'creator' ? '2px solid var(--color-primary)' : '1px solid var(--color-border)',
                position: 'relative'
              }}>
                {pkg.id === 'creator' && (
                  <div style={{
                    position: 'absolute',
                    top: '-12px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: 'var(--color-primary)',
                    color: 'white',
                    padding: '4px 12px',
                    borderRadius: '12px',
                    fontSize: '0.75rem',
                    fontWeight: 600
                  }}>
                    BEST VALUE
                  </div>
                )}

                <h3 style={{ marginTop: pkg.id === 'creator' ? '12px' : 0 }}>
                  {pkg.name}
                </h3>

                <div style={{ fontSize: '2rem', fontWeight: 700, margin: '16px 0' }}>
                  ${pkg.price.toFixed(2)}
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <div style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '4px' }}>
                    {pkg.credits.toLocaleString()} credits
                  </div>
                  {pkg.bonusCredits > 0 && (
                    <div style={{ color: 'var(--color-success)', fontSize: '0.9rem' }}>
                      + {pkg.bonusCredits.toLocaleString()} bonus credits
                    </div>
                  )}
                </div>

                <div style={{
                  fontSize: '0.85rem',
                  color: 'var(--color-text-secondary)',
                  marginBottom: '16px',
                  paddingTop: '16px',
                  borderTop: '1px solid var(--color-border)'
                }}>
                  ≈ {Math.floor((pkg.credits + pkg.bonusCredits) / 20000)} complete workflows
                </div>

                <button
                  onClick={() => handlePurchaseCredits(pkg.id)}
                  className="btn btn-primary"
                  style={{ width: '100%' }}
                >
                  Purchase
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Subscription Plans Tab */}
      {activeTab === 'subscription' && (
        <div>
          <div style={{ marginBottom: '24px' }}>
            <h2>Subscription Plans</h2>
            <p style={{ color: 'var(--color-text-secondary)' }}>
              Choose a plan to access the Vitisiti platform. Credits are purchased separately.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
            {subscriptionTiers.map((tier) => (
              <div key={tier.id} className="card" style={{
                border: tier.id === 'pro' ? '2px solid var(--color-primary)' : '1px solid var(--color-border)',
                position: 'relative'
              }}>
                {tier.id === 'pro' && (
                  <div style={{
                    position: 'absolute',
                    top: '-12px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: 'var(--color-primary)',
                    color: 'white',
                    padding: '4px 12px',
                    borderRadius: '12px',
                    fontSize: '0.75rem',
                    fontWeight: 600
                  }}>
                    POPULAR
                  </div>
                )}

                <h3 style={{
                  marginTop: tier.id === 'pro' ? '12px' : 0,
                  textTransform: 'capitalize'
                }}>
                  {tier.name}
                </h3>

                <div style={{ fontSize: '2rem', fontWeight: 700, margin: '16px 0' }}>
                  ${tier.price.toFixed(2)}
                  <span style={{ fontSize: '1rem', fontWeight: 400, color: 'var(--color-text-secondary)' }}>
                    /month
                  </span>
                </div>

                <ul style={{
                  listStyle: 'none',
                  padding: 0,
                  margin: '16px 0',
                  fontSize: '0.95rem'
                }}>
                  {tier.features.map((feature, idx) => (
                    <li key={idx} style={{
                      marginBottom: '8px',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '8px'
                    }}>
                      <span style={{ color: 'var(--color-success)', flexShrink: 0 }}>✓</span>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => handleSubscribe(tier.id)}
                  className="btn"
                  style={{
                    width: '100%',
                    background: tier.id === subscriptionTier ? 'var(--color-border)' : 'var(--color-primary)',
                    color: tier.id === subscriptionTier ? 'var(--color-text)' : 'white'
                  }}
                  disabled={tier.id === subscriptionTier}
                >
                  {tier.id === subscriptionTier ? 'Current Plan' : 'Subscribe'}
                </button>
              </div>
            ))}
          </div>

          {subscriptionStatus === 'active' && (
            <div className="card" style={{ marginTop: '32px', background: '#f0f9ff' }}>
              <h3>Manage Subscription</h3>
              <p style={{ color: 'var(--color-text-secondary)', marginBottom: '16px' }}>
                You can cancel your subscription at any time. You'll continue to have access until the end of your billing period.
              </p>
              <button
                onClick={async () => {
                  if (confirm('Are you sure you want to cancel your subscription?')) {
                    try {
                      await fetch('http://localhost:3001/api/stripe/cancel-subscription', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ userId: user.uid })
                      });
                      alert('Subscription canceled. You will have access until the end of your billing period.');
                      window.location.reload();
                    } catch (error) {
                      console.error('Error canceling subscription:', error);
                      alert('Error canceling subscription. Please try again.');
                    }
                  }
                }}
                className="btn btn-secondary"
              >
                Cancel Subscription
              </button>
            </div>
          )}
        </div>
      )}

      {/* Usage Info */}
      <div className="card" style={{ marginTop: '32px', background: '#fef3c7' }}>
        <h3>How Credits Work</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginTop: '16px' }}>
          <div>
            <div style={{ fontWeight: 600, marginBottom: '4px' }}>Text Generation</div>
            <div style={{ fontSize: '0.9rem', color: 'var(--color-text-secondary)' }}>
              ~66 credits per 1,000 tokens
            </div>
          </div>
          <div>
            <div style={{ fontWeight: 600, marginBottom: '4px' }}>Image Generation</div>
            <div style={{ fontSize: '0.9rem', color: 'var(--color-text-secondary)' }}>
              4,290 credits per image
            </div>
          </div>
          <div>
            <div style={{ fontWeight: 600, marginBottom: '4px' }}>Complete Workflow</div>
            <div style={{ fontSize: '0.9rem', color: 'var(--color-text-secondary)' }}>
              ~20,000 credits ($0.20)
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
